import {
  UpdateReqData,
  Request,
  checkboxOption,
  RequestQuestion,
  RequestQuestionDataDate,
  RequestQuestionDataFile,
} from 'utils/customTypes';
import { filter, get, isPlainObject } from 'lodash';
import { PickerFileMetadata } from 'filestack-js';
import { SYSTEM_MAX_BYTES } from 'utils/fileSize';

const isBlank = (value: unknown) =>
  value === undefined || value === null || value === '';

/**
 * True when `question` is required but has no answer — i.e. it must block
 * submission. Callers (RequestPage, RequestSidePanel) `filter` questions
 * through this and disable Submit when anything comes back.
 *
 * Every question type must be handled explicitly. CGP-220: the previous
 * implementation fell back to `!question.data.value`, so any type whose answer
 * is stored as an object (`range`, date ranges, multiselect dropdowns) read as
 * truthy-therefore-answered and was never enforced. Adding a question type
 * without a case here re-opens that hole — `default` only covers answers
 * stored as a plain scalar.
 */
export const validateRequestRequiredQuestion = (question: RequestQuestion) => {
  if (!question.data.isRequired) {
    return false;
  }
  const value = question.data.value;
  switch (question.type) {
    // A toggle is always answered: `false` is a legitimate answer.
    case 'toggle':
      return false;
    case 'dropdown':
    case 'radio': {
      const selection = get(value, 'value');
      // A multiselect dropdown stores an array of picked options...
      if (Array.isArray(selection)) {
        return !selection.length;
      }
      // ...a single one a `{ value, display }` pair, which survives clearing
      // as an object with undefined members...
      if (isPlainObject(selection)) {
        return isBlank(get(selection, 'value'));
      }
      // ...and a radio the id of the checked option.
      return isBlank(selection);
    }
    case 'checkbox':
      return !get(value, 'items', []).some(
        (item: checkboxOption) => item.checked
      );
    case 'file':
      return !get(value, 'length');
    case 'date':
      // A date range is only complete once both ends are picked, matching the
      // two required fields IntakeDateQuestion renders for `data.range`.
      return (question.data as RequestQuestionDataDate).range
        ? isBlank(get(value, 'startDate')) || isBlank(get(value, 'endDate'))
        : isBlank(value);
    case 'range':
      // `data.value` is `{ from, to, value }`; only `value` is the answer.
      return isBlank(get(value, 'value'));
    default:
      // Scalar answers (customTextArea, url, number). `0` is an answer.
      return isBlank(value);
  }
};

export const questionHasOversizeFile = (question: RequestQuestion) => {
  if (question.type !== 'file') return false;
  const limit =
    (question.data as RequestQuestionDataFile).maxFileSizeBytes ??
    SYSTEM_MAX_BYTES;
  const files = question.data.value;
  return (
    Array.isArray(files) &&
    files.some(
      (file: PickerFileMetadata) =>
        typeof file.size === 'number' && file.size > limit
    )
  );
};

export const validateRequiredFields = (
  title: string,
  requestData: Request,
  updatedReqData: UpdateReqData,
  shouldUseUserBusinessTeamAsDefault: boolean,
  isForm: boolean,
  enforceFileSizeLimits = false
) => {
  const isTitleValid = title !== '';
  let areQuestionsValid = true;
  if (updatedReqData && updatedReqData.requestQuestions) {
    const requiredQuestionsWithoutAnswers = filter(
      updatedReqData.requestQuestions,
      validateRequestRequiredQuestion
    );
    const oversizeFileQuestions = enforceFileSizeLimits
      ? filter(updatedReqData.requestQuestions, questionHasOversizeFile)
      : [];
    if (
      requiredQuestionsWithoutAnswers.length ||
      oversizeFileQuestions.length
    ) {
      areQuestionsValid = false;
    }
  }
  let areBusinessTeamsValid = true;
  if (!shouldUseUserBusinessTeamAsDefault) {
    const updatedBusinessTeams = get(
      updatedReqData,
      'requestAttributes.businessTeams.length'
    );
    const prevBusinessTeams = get(requestData, 'businessTeams.length');
    if (isForm && !updatedBusinessTeams) {
      areBusinessTeamsValid = false;
    } else if (
      (updatedBusinessTeams <= 0 || updatedBusinessTeams === undefined) &&
      (prevBusinessTeams <= 0 || prevBusinessTeams === undefined)
    ) {
      areBusinessTeamsValid = false;
    }
  }
  const isRequesterValid =
    get(updatedReqData, 'requestAttributes.requester_id') !== '';
  return (
    isTitleValid &&
    areQuestionsValid &&
    areBusinessTeamsValid &&
    isRequesterValid
  );
};
